<template>
  <NavigationPaneProvider :position="storePosition">
    <InnerNavigationPane :items="items" :profile="profileWithVNode" />
  </NavigationPaneProvider>
</template>

<script lang="ts">
import { defineComponent, type PropType, watch, computed, type VNode, h } from 'vue'
import { type NavItem, type NavigationPaneConfig, type Position } from './types/NavigationPane'
import NavigationPaneProvider from './NavigationPaneProvider.vue'
import InnerNavigationPane from './internal/InnerNavigationPane.vue'
import { navigationStore } from './store/navigationStore'

export interface Profile {
  /** User's name */
  name: string
  /** User's tag or status */
  tag: string
  /** User's profile image - can be a Vue component/VNode, string path, or null */
  image: VNode | (() => VNode) | string | null
  /** User's rank number */
  rank?: number
}

export default defineComponent({
  name: 'NavigationPane',
  components: {
    NavigationPaneProvider,
    InnerNavigationPane
  },
  props: {
    /**
     * List of navigation items.
     * Each item can have:
     * - id: unique identifier
     * - icon: optional icon component
     * - label: text to display
     * - onClick: function to call when clicked
     * - href: URL to navigate to (will render as a link instead of button)
     * - showDivider: whether to show a divider after this item
     * - disabled: whether the item is disabled (won't be clickable)
     */
    items: {
      type: Array as PropType<NavItem[]>,
      required: true
    },

    /** User profile details */
    profile: {
      type: Object as PropType<Profile>,
      required: true
    },

    /** Config for the navigation menu */
    config: {
      type: Object as PropType<NavigationPaneConfig>,
      default: () => ({})
    },

    /** Selected item ID */
    selectedItemId: {
      type: String,
      default: undefined
    }
  },
  setup(props) {
    // Update store with config when it changes
    watch(
      () => props.config,
      (newConfig) => {
        if (newConfig) {
          navigationStore.updateConfig({
            ...newConfig
          })
        }
      },
      { deep: true, immediate: true }
    )

    // Update selected item when it changes from props
    watch(
      () => props.selectedItemId,
      (newSelectedItemId) => {
        if (newSelectedItemId !== undefined) {
          navigationStore.setSelectedItemId(newSelectedItemId)
        }
      },
      { immediate: true }
    )

    // Compute the position for the provider
    const storePosition = computed((): Position => navigationStore.state.position)

    // Simply pass the profile directly without modifications
    const profileWithVNode = computed(() => props.profile)

    return {
      storePosition,
      profileWithVNode
    }
  }
})
</script>
